import React, { useEffect, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import { de } from "date-fns/locale";

import {
    asString,
    useClearFieldError,
    useFieldError,
    useRegisterFieldValidation,
} from "../core/formValidation";
import { createDateValidator } from "../validation/defaultValidators";

export interface CalendarFieldProps {
    label: string;
    name: string;
    daysInFuture: number;
    allowedWeekdays?: number[];

    defaultValue?: Date;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    validate?: (value: string) => boolean;
    validationMessage?: string;
}

export const CalendarField: React.FC<CalendarFieldProps> = ({
    label,
    name,
    daysInFuture,
    allowedWeekdays = [1, 2, 3, 4],
    defaultValue,
    required = false,
    disabled = false,
    className,
    validate,
    validationMessage,
}) => {
    const [selected, setSelected] = useState<Date | undefined>(defaultValue);

    const registerFieldValidation = useRegisterFieldValidation();
    const clearFieldError = useClearFieldError();
    const fieldError = useFieldError(name);

    const effectiveValidator = useMemo(
        () => validate ?? createDateValidator(required),
        [required, validate]
    );

    useEffect(() => {
        if (!registerFieldValidation || disabled) {
            return;
        }

        return registerFieldValidation({
            name,
            message: validationMessage,
            validate: (value) => effectiveValidator(asString(value)),
        });
    }, [
        disabled,
        effectiveValidator,
        name,
        registerFieldValidation,
        validationMessage,
    ]);

    const today = useMemo(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    }, []);

    const lastDay = useMemo(() => {
        const date = new Date(today);
        date.setDate(date.getDate() + daysInFuture);
        return date;
    }, [today, daysInFuture]);

    const handleSelect = (date: Date | undefined): void => {
        setSelected(date);
        clearFieldError(name);
    };

    return (
        <div className={["form-field", className].filter(Boolean).join(" ")}>
            <label htmlFor={name}>{label}</label>

            <span className="wpcf7-form-control-wrap" data-name={name}>
                <DayPicker
                    mode="single"
                    selected={selected}
                    onSelect={handleSelect}
                    startMonth={today}
                    endMonth={lastDay}
                    weekStartsOn={1}
                    locale={de}
                    className={fieldError ? "wpcf7-not-valid" : undefined}
                    disabled={
                        disabled
                            ? true
                            : [
                                  { before: today },
                                  { after: lastDay },
                                  (date) =>
                                      !allowedWeekdays.includes(
                                          date.getDay()
                                      ),
                              ]
                    }
                />

                <input
                    id={name}
                    type="hidden"
                    name={name}
                    value={selected?.toISOString() ?? ""}
                    required={required}
                    disabled={disabled}
                />

                {fieldError ? (
                    <span className="wpcf7-not-valid-tip" aria-hidden="true">
                        {fieldError}
                    </span>
                ) : null}
            </span>
        </div>
    );
};

